import { MagpieConnection } from 'magpie-lib/connection';
import { defineMessage, defineTopic } from 'magpie-lib/models';
import Raven from 'raven-js';
import * as t from '../utils/schema';
import enviro from 'enviro';
import { Metrics } from '../utils/metrics';
import { devtoolsFlags } from '../features/devtools/flags';
import { getHubspotFrameOriginsForCurrentEnv, getHubspotPreviewOriginsForCurrentEnv, isCurrentWindowHubspotPreview, isHubspotFrameOrigin, isPortalHubspotPreviewOrigin } from '../utils/magpie';
/**
 * Magpie doesnt really make schema validation errors visible, so this helper
 * will log warnings if values being sent don't match the schema. This typically
 * happens when deep merging objects into the state.
 */
const prevalidatePublish = ({
  value,
  schema,
  context
}) => {
  const env = enviro.get('ENV');
  const isDeployed = enviro.deployed();
  if (isDeployed && env === 'production') {
    return;
  }
  const check = t.rawExecuteSchema({
    quick: false,
    schemaViolations: []
  }, schema, value);
  if (check.type === 'fail') {
    if (devtoolsFlags.leaderFollowerValidation) {
      console.warn('ReportNewLeaderValue validation error', check.schemaViolations, value, context);
    }
    Metrics.counter('magpie-publish-validation-error').increment();
  }
};

/**
 * postMessage transport for the asset preview iframe in LayoutBuilderUI.
 *
 * The preview is hosted on a separate `*.hubspotpreview(qa)?-*.com` domain,
 * which causes compatibility issues for Magpie's cross-frame sync. Since we
 * appear to be the only consumer hitting this topology, it's not worth the
 * investment in Magpie to support it; instead we fall back to a dedicated
 * postMessage channel between the HubSpot frame (leader) and the preview
 * iframe (follower) for this case only.
 *
 * When ContentEditorV2 lands we expect to be able to load our components in
 * the parent frame directly, at which point this transport should become
 * redundant and can be removed.
 */
const POST_MESSAGE_SOURCE = 'collaboration-sidebar/magpie-strategy';
const postMessageEnvelopeSchema = t.union(t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('ReportNewLeaderValue'),
  payload: t.object({
    value: t.unknown
  })
}), t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('RequestNewLeaderValue')
}), t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('RequestUpdateLeaderValue'),
  payload: t.object({
    value: t.unknown
  })
}));
const isPostMessageEnvelope = data => t.validate(postMessageEnvelopeSchema, data);
const POST_MESSAGE_LOG_PREFIX = '[magpie-strategy postMessage]';
const logPostMessageEvent = (direction, role, key, type, extra) => {
  if (!devtoolsFlags.previewPostMessageLogging) {
    return;
  }
  console.log(POST_MESSAGE_LOG_PREFIX, Object.assign({
    role,
    direction,
    type,
    key
  }, extra));
};
const createPostMessageTransport = ({
  key
}) => {
  const broadcastToPreviewFrames = envelope => {
    const previewOrigins = getHubspotPreviewOriginsForCurrentEnv();
    for (let i = 0; i < window.frames.length; i++) {
      const frame = window.frames[i];
      for (const targetOrigin of previewOrigins) {
        try {
          frame.postMessage(envelope, targetOrigin);
          logPostMessageEvent('send', 'leader', key, envelope.type, {
            targetOrigin
          });
        } catch (error) {
          Metrics.counter('magpie-postmessage-send-error').increment();
          Raven.captureException(error);
        }
      }
    }
  };
  return {
    startLeaderListener: ({
      onRequestNewLeaderValue,
      onRequestUpdateLeaderValue
    }) => {
      const handler = event => {
        if (!isPortalHubspotPreviewOrigin(event.origin)) {
          return;
        }
        if (!isPostMessageEnvelope(event.data)) {
          return;
        }
        if (event.data.key !== key) {
          return;
        }
        logPostMessageEvent('receive', 'leader', key, event.data.type, {
          origin: event.origin
        });
        if (event.data.type === 'RequestNewLeaderValue') {
          broadcastToPreviewFrames({
            source: POST_MESSAGE_SOURCE,
            key,
            type: 'ReportNewLeaderValue',
            payload: {
              value: onRequestNewLeaderValue()
            }
          });
          return;
        }
        if (event.data.type === 'RequestUpdateLeaderValue') {
          onRequestUpdateLeaderValue(event.data.payload.value);
        }
      };
      window.addEventListener('message', handler);
      return () => {
        window.removeEventListener('message', handler);
      };
    },
    broadcastFromLeader: value => {
      broadcastToPreviewFrames({
        source: POST_MESSAGE_SOURCE,
        key,
        type: 'ReportNewLeaderValue',
        payload: {
          value
        }
      });
    },
    startFollowerListener: onNewLeaderValue => {
      const handler = event => {
        if (event.source !== window.parent) {
          return;
        }
        if (!isHubspotFrameOrigin(event.origin)) {
          return;
        }
        if (!isPostMessageEnvelope(event.data)) {
          return;
        }
        if (event.data.key !== key) {
          return;
        }
        if (event.data.type !== 'ReportNewLeaderValue') {
          return;
        }
        logPostMessageEvent('receive', 'follower', key, event.data.type, {
          origin: event.origin
        });
        onNewLeaderValue(event.data.payload.value);
      };
      window.addEventListener('message', handler);
      return () => {
        window.removeEventListener('message', handler);
      };
    },
    sendFromFollower: envelope => {
      if (!window.parent) {
        return;
      }
      for (const targetOrigin of getHubspotFrameOriginsForCurrentEnv()) {
        try {
          window.parent.postMessage(envelope, targetOrigin);
          logPostMessageEvent('send', 'follower', key, envelope.type, {
            targetOrigin
          });
        } catch (error) {
          Metrics.counter('magpie-postmessage-send-error').increment();
          Raven.captureException(error);
        }
      }
    }
  };
};

/**
 * Creates a Magpie-based synchronization strategy for leader-follower stores.
 *
 * This function sets up the necessary Magpie messages and topic for communication
 * between leader and follower stores, providing a reusable strategy that can be
 * used with different store implementations.
 *
 * @param key - A unique string key to identify this leader-follower pair
 * @param audience - The Magpie audience to use for messaging
 * @returns A SyncStrategy object with methods for store communication
 *
 * @example
 * ```typescript
 * import { CollaborationSidebarAudience } from '../utils/magpie';
 * import { createLeaderFollowerStore } from './leaderFollowerStore';
 *
 * const strategy = magpieStrategy<{ count: number }>({
 *   key: 'counter',
 *   audience: CollaborationSidebarAudience,
 *   valueSchema: t.object({ count: t.number })
 * });
 *
 * // Wire into leader/follower stores
 * const { makeLeader, makeFollower } = createLeaderFollowerStore<{
 *   count: number;
 * }>({
 *   syncStrategy: strategy,
 * });
 *
 * // Leader store instance
 * const leader = makeLeader({ count: 0 });
 *
 * // Follower store instance
 * const follower = makeFollower();
 *
 * // Subscribe to updates delivered via Magpie
 * const unsubscribeLeader = strategy.subscribeToLeaderUpdate((value) => {
 *   // follower -> leader updates
 *   console.log('Leader received', value);
 * });
 * const unsubscribeFollower = strategy.subscribeToFollowerUpdate((value) => {
 *   // leader -> follower updates
 *   console.log('Follower received', value);
 * });
 *
 * // Follower can request current state from leader
 * const unsubscribeRequest = strategy.subscribeToLeaderUpdateRequest(() => {
 *   strategy.updateFollower(leader.getState());
 * });
 * strategy.requestStateFromLeader();
 *
 * // Cleanup
 * unsubscribeLeader();
 * unsubscribeFollower();
 * unsubscribeRequest();
 * leader.cleanup();
 * follower.cleanup();
 * ```
 */

export const magpieStrategy = ({
  key,
  audience,
  valueSchema
}) => {
  const ReportNewLeaderValueMessage = defineMessage({
    type: `ReportNewLeaderValue`,
    payloadSchema: t.object({
      value: valueSchema,
      key: t.literal(key)
    })
  });
  const RequestNewLeaderValueMessage = defineMessage({
    type: `RequestNewLeaderValue`,
    reply: ReportNewLeaderValueMessage,
    payloadSchema: t.object({
      key: t.literal(key)
    })
  });
  const RequestUpdateLeaderValueMessage = defineMessage({
    type: `RequestUpdateLeaderValue`,
    payloadSchema: t.object({
      value: valueSchema,
      key: t.literal(key)
    })
  });
  const LeaderFollowerTopic = defineTopic({
    name: `LeaderFollowerTopic`,
    messages: [ReportNewLeaderValueMessage, RequestNewLeaderValueMessage, RequestUpdateLeaderValueMessage],
    audience,
    enableRuntimeValidation: false
  });
  const publishErrorConfig = {
    onValidationError: error => {
      Metrics.counter('magpie-publish-validation-error').increment();
      Raven.captureException(error);
    },
    onError: error => {
      Metrics.counter('magpie-publish-error').increment();
      Raven.captureException(error);
    }
  };
  const subscriptionConfig = {
    onError: error => {
      Metrics.counter('magpie-subscription-error').increment();
      Raven.captureException(error);
    }
  };
  const postMessageTransport = createPostMessageTransport({
    key
  });
  return {
    setupLeader: ({
      onNewLeaderValueRequest,
      onUpdateLeaderValueRequest
    }) => {
      const topic = MagpieConnection.get().forTopic(LeaderFollowerTopic);
      const unsubscribeRequestNewLeaderValue = topic.on(RequestNewLeaderValueMessage.getType(), (_, api) => {
        const newLeaderValue = onNewLeaderValueRequest();
        api.reply(ReportNewLeaderValueMessage.create({
          value: newLeaderValue,
          key
        }));
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      const unsubscribeRequestUpdateLeaderValue = topic.on(RequestUpdateLeaderValueMessage.getType(), message => {
        onUpdateLeaderValueRequest(message.payload.value);
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      const stopPostMessageListener = postMessageTransport.startLeaderListener({
        onRequestNewLeaderValue: onNewLeaderValueRequest,
        onRequestUpdateLeaderValue: onUpdateLeaderValueRequest
      });
      return {
        reportNewLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'reportNewLeaderValue'
          });
          topic.publish(ReportNewLeaderValueMessage.create({
            value: newValue,
            key
          }), publishErrorConfig);
          postMessageTransport.broadcastFromLeader(newValue);
        },
        cleanup: () => {
          unsubscribeRequestNewLeaderValue();
          unsubscribeRequestUpdateLeaderValue();
          stopPostMessageListener();
        }
      };
    },
    setupFollower: ({
      onNewLeaderValue
    }) => {
      if (isCurrentWindowHubspotPreview()) {
        const stopPostMessageListener = postMessageTransport.startFollowerListener(onNewLeaderValue);
        postMessageTransport.sendFromFollower({
          source: POST_MESSAGE_SOURCE,
          key,
          type: 'RequestNewLeaderValue'
        });
        return {
          requestUpdateLeaderValue: newValue => {
            prevalidatePublish({
              value: newValue,
              schema: valueSchema,
              context: 'requestUpdateLeaderValue'
            });
            postMessageTransport.sendFromFollower({
              source: POST_MESSAGE_SOURCE,
              key,
              type: 'RequestUpdateLeaderValue',
              payload: {
                value: newValue
              }
            });
          },
          cleanup: () => {
            stopPostMessageListener();
          }
        };
      }
      const topic = MagpieConnection.get().forTopic(LeaderFollowerTopic);
      const unsubscribeNewLeaderValue = topic.on(ReportNewLeaderValueMessage.getType(), message => {
        onNewLeaderValue(message.payload.value);
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      topic.publish(RequestNewLeaderValueMessage.create({
        key
      }), Object.assign({}, publishErrorConfig, {
        onReply: reply => {
          onNewLeaderValue(reply.payload.value);
        }
      }));
      return {
        requestUpdateLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'requestUpdateLeaderValue'
          });
          topic.publish(RequestUpdateLeaderValueMessage.create({
            value: newValue,
            key
          }), publishErrorConfig);
        },
        cleanup: () => {
          unsubscribeNewLeaderValue();
        }
      };
    }
  };
};